/**
 * Either a button that queues research, or a state chip saying the work is already in hand.
 *
 * Work already in hand reads as a state, not as a button that does nothing. The button is never
 * pre-disabled for any other reason: queueing a register with no official source is refused by
 * the server with a sentence naming the register and the matter, and that sentence is better
 * than an inert control.
 */

import { Link } from 'react-router';
import { Button } from '~/ui/Button';
import { useQueueResearch } from '~/api/mutations';
import { useToast } from '~/hooks/useToast';
import { ApiError, problemsOf } from '~/api/client';
import { fmtDate, fmtList } from '~/lib/format';
import { routes } from '~/lib/routes';
import type { OpenQueueRef, VocabularyTerm } from '~/api/schema';
import styles from '../jurisdictions/Register.module.css';

export interface QueueActionButtonProps {
  code: string;
  name: string;
  matters: readonly string[];
  matterVocabulary: readonly VocabularyTerm[];
  openEntry: OpenQueueRef | null;
  label?: string;
  /** Set on a row so the control does not also follow the row's link. */
  stopPropagation?: boolean;
}

const STATE_LABEL: Record<string, string> = { queued: 'Queued', running: 'Running' };

export function QueueActionButton({
  code,
  name,
  matters,
  matterVocabulary,
  openEntry,
  label = 'Queue AI research',
  stopPropagation = false,
}: QueueActionButtonProps) {
  const queue = useQueueResearch();
  const toast = useToast();

  const labelFor = (key: string) =>
    matterVocabulary.find((term) => term.code === key)?.label ?? key;

  if (openEntry) {
    const scope = fmtList(openEntry.matters.map(labelFor));
    return (
      <span className={styles.queueState}>
        {STATE_LABEL[openEntry.state] ?? openEntry.state}
        <span className="visually-hidden">
          {` — ${scope}, added ${fmtDate(openEntry.added)}`}
        </span>
      </span>
    );
  }

  return (
    <Button
      size="sm"
      pending={queue.isPending}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
        queue.mutate(
          { code, matters: [...matters], note: '' },
          {
            onSuccess: () =>
              toast.show('ok', `${name} queued for ${fmtList(matters.map(labelFor))} research.`),
            onError: (error) => {
              for (const problem of problemsOf(error)) toast.show('error', problem);
              if (error instanceof ApiError && error.code === 'research_blocked') {
                toast.show(
                  'error',
                  'Add an official source on the register’s sources tab before researching it.',
                );
              }
            },
          },
        );
      }}
    >
      {label}
    </Button>
  );
}

/** The link the research-blocked sentence points at. */
export function SourcesLink({ code, matter }: { code: string; matter: string }) {
  return <Link to={routes.sources(code, matter)}>official sources</Link>;
}
